import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleKeys } from '@entities/Module';
import {
  addCard,
  AddCardError,
  fetchFreshWords,
  fetchMyWords,
  fetchWordsForUnit,
  hideWord,
  type MyWordsKind,
  restoreWord,
} from '../api/wordsApi';

export const wordKeys = {
  byUnit: (unitId: string) => ['words', unitId] as const,
  /** «Слова» страницы «Слова и модули»; под общим ['words'] — его сбрасывает правка карточек слова. */
  mineRoot: ['words', 'mine'] as const,
  mine: (q: string, kind: MyWordsKind, moduleId: string | null) => ['words', 'mine', { q, kind, moduleId }] as const,
  /** Добавленное сегодня и не тренированное — тоже под `mineRoot`: его перечитывают вместе со «Словами». */
  fresh: ['words', 'mine', 'fresh'] as const,
};

/** Порция «Слов»: 30 строк — экран телефона с запасом, «Показать ещё» добирает следующую. */
export const MY_WORDS_PAGE = 30;

/** Слова ученика (все или одного модуля) с поиском и фильтром, порциями; при смене поиска старый список виден до
 *  ответа. */
export function useMyWordsQuery(q: string, kind: MyWordsKind, moduleId: string | null) {
  return useInfiniteQuery({
    queryKey: wordKeys.mine(q, kind, moduleId),
    queryFn: ({ pageParam }) =>
      fetchMyWords({ page: pageParam, size: MY_WORDS_PAGE, q: q || undefined, kind, moduleId: moduleId ?? undefined }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page < last.pages ? last.page + 1 : undefined),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

/** Добавленное сегодня и ещё не тренированное (2026-10-28). Перечитывается при каждом входе на страницу: после
 *  тренировки слова уходят, и кнопка «Тренировать новые» с записями «Сегодня» — вместе с ними. */
export function useFreshWordsQuery() {
  return useQuery({ queryKey: wordKeys.fresh, queryFn: fetchFreshWords, staleTime: 0 });
}

export function useWordsByUnitQuery(unitId: string | null | undefined) {
  return useQuery({
    queryKey: wordKeys.byUnit(unitId ?? ''),
    queryFn: () => fetchWordsForUnit(unitId!),
    enabled: !!unitId,
  });
}

interface AddCardPayload {
  word: string;
  unitId: string;
}

export function useAddCardMutation() {
  const qc = useQueryClient();
  return useMutation<void, AddCardError, AddCardPayload>({
    mutationFn: addCard,
    onSuccess: (_void, vars) => {
      void qc.invalidateQueries({ queryKey: wordKeys.byUnit(vars.unitId) });
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}

interface HideWordPayload {
  unitId: string;
  cardId: string;
  meaningId: string;
}

/**
 * Скрыть карточку из модуля (soft-hide). Инвалидируем список слов модуля (карточка
 * уезжает во вкладку «Скрытые») и список модулей (счётчики). Тот же ключ ['words', id]
 * шарят ModuleWords / ModuleKpis / превью / пикер тренировки — все пересчитаются.
 */
export function useHideWordMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, HideWordPayload>({
    mutationFn: ({ cardId, meaningId }) => hideWord(cardId, meaningId),
    onSuccess: (_void, vars) => {
      void qc.invalidateQueries({ queryKey: wordKeys.byUnit(vars.unitId) });
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}

export function useRestoreWordMutation() {
  const qc = useQueryClient();
  return useMutation<void, Error, HideWordPayload>({
    mutationFn: ({ cardId, meaningId }) => restoreWord(cardId, meaningId),
    onSuccess: (_void, vars) => {
      void qc.invalidateQueries({ queryKey: wordKeys.byUnit(vars.unitId) });
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
    },
  });
}
